/**
 * What an eScript cart hydration has to report to the shopper: a promo code that could not be
 * applied, and items that could not be added.
 *
 * Each report is emitted while the checkout skeleton is up, long before the order summary that
 * reads it mounts, so each is held in a `BehaviorSubject` rather than a `createSignal`: the value
 * has to survive both the wait and any moment where the subscriber count reaches zero, which resets
 * a refCounted `bind`/`state` (sc-137931, sc-142675).
 *
 * Because the subjects hold their values for the lifetime of the page, whatever stops describing the
 * cart in front of the shopper has to retire the report itself: a new hydration calls
 * `clearEScriptReports` before it starts, and `clearEScriptReportsOnOrder$` below covers the order
 * that empties the cart without one.
 */
import { isApolloError } from '@apollo/client'
import { bind } from '@react-rxjs/core'
import { BehaviorSubject, tap } from 'rxjs'

import { orderPlaced$ } from '../cart/state/order'

type EScriptError = {
  _tag: 'InvalidPromoCode'
  error_message: string
}

type EScriptPromoCode = {
  code: string
}

export const invalidCouponCodeError = (err: unknown): string | undefined =>
  err instanceof Error && isApolloError(err)
    ? err.graphQLErrors.find((graphqlError) =>
        graphqlError.message.includes(`coupon code isn't valid`),
      )?.message
    : undefined

const eScriptErrors$ = new BehaviorSubject<ReadonlyArray<EScriptError> | undefined>(undefined)

export const setEScriptErrors = (errors: ReadonlyArray<EScriptError>): void =>
  eScriptErrors$.next(errors)

export const [useLatestEScriptErrors, latestEScriptErrors$] = bind(eScriptErrors$, undefined)

const eScriptPromoUsed$ = new BehaviorSubject<EScriptPromoCode | undefined>(undefined)

export const setEScriptPromoUsed = (promoCode: EScriptPromoCode | undefined): void =>
  eScriptPromoUsed$.next(promoCode)

export const [useLatestEScriptPromoUsed, latestEScriptPromoUsed$] = bind(
  eScriptPromoUsed$,
  undefined,
)

const eScriptDroppedSkus$ = new BehaviorSubject<ReadonlyArray<string> | undefined>(undefined)

export const setEScriptDroppedSkus = (skus: ReadonlyArray<string>): void =>
  eScriptDroppedSkus$.next(skus)

export const [useLatestEScriptDroppedSkus, latestEScriptDroppedSkus$] = bind(
  eScriptDroppedSkus$,
  undefined,
)

/**
 * Discard every report from a previous hydration. Call before a new one starts.
 */
export const clearEScriptReports = (): void => {
  eScriptErrors$.next(undefined)
  eScriptPromoUsed$.next(undefined)
  eScriptDroppedSkus$.next(undefined)
}

/**
 * Retires every report above once the order is placed.
 *
 * Without this a report outlives the cart it describes. Placing an order empties the cart, so backing
 * out of `/checkout/confirmation` puts the shopper on `EmptyCartCheckout`, which renders the same
 * alerts the confirmation suppresses — reading `COQ200 could not be added to your cart` about an
 * order they have already placed.
 *
 * No hydration reaches it either: `useDispensaryUpdateSession` marks itself `called` once, so
 * nothing on the way back from the confirmation runs `updateCartDispensary` again to reach the
 * `clearEScriptReports` at its head.
 *
 * Subscribed from `CheckoutRoutes`, the last eScript subscription held there now that the reports
 * hold themselves — this one is an effect rather than a value that would otherwise be dropped.
 */
export const clearEScriptReportsOnOrder$ = orderPlaced$.pipe(tap(clearEScriptReports))
